import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import toast from 'react-hot-toast';
import { useTranslation, Trans } from 'react-i18next';

import {
  CheckCircle as CheckCircleIcon,
  AddCircle as AddCircleIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  ArrowRight as ArrowRightIcon,
  ArrowLeft as ArrowLeftIcon,
} from '@mui/icons-material/';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import '../../../assets/css/buttons.css';

// constants
const CLOSE_BEFORE_MS = 25_000; // 25 s
const GAMES_PER_PAGE = 16;
const BET_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const CARD_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// hh:mm(:ss) -> Date for today
const timeStringToTodayDate = (t) => {
  const [h, m, s = 0] = String(t ?? '0:0:0')
    .split(':')
    .map(Number);
  const d = new Date();
  d.setHours(h || 0, m || 0, s || 0, 0);
  return d;
};

function PaymentScreen() {
  const { t } = useTranslation();
  const {
    updateIsLoadingScreen,
    updateIsSellerOpen,
    updateIsPapirXtippOpen,
    hall,
    session,
    userInfo,
    parentInfo,
    updateTransactions,
    operator,
    printer,
    updateShowUserInfo,
    updateShowUserInfoSibling,
    updateShowUserInfoOK,
    updateShowUserInfoWG,
    updateShowUserInfoSiblingOK,
    closeTime,
  } = useContext(AppContext);

  const { socket } = useContext(SocketContext);

  // state
  const [rawGames, setRawGames] = useState([]);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [nextGame, setNextGame] = useState(null);
  const [gamesPage, setGamesPage] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [ticketsList, setTicketList] = useState([]);

  const [bet, setBet] = useState(0);
  const [cards, setCards] = useState(0);

  const [isRequesting, setIsRequesting] = useState(false);
  const [isReloadNeeded, setIsReloadNeeded] = useState(false);

  useEffect(() => {
    updateShowUserInfo(false);
    updateShowUserInfoSibling(false);
    updateShowUserInfoOK(false);
    updateShowUserInfoWG(false);
    updateShowUserInfoSiblingOK(false);
  }, []);

  // current time (ticks every second)
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // freeze the UI "clock" while a request is in-flight
  const [frozenAt, setFrozenAt] = useState(null);
  const timeBase = frozenAt ?? now;

  useEffect(() => {
    if (isRequesting) {
      setFrozenAt((prev) => prev ?? now);
    } else {
      setFrozenAt(null);
    }
  }, [isRequesting, now]);

  useEffect(() => {
    if (!isRequesting) return;
    const ids = selectedGameIdsRef.current;
    if (!ids || !ids.length) return;
    const someClosed = openGames
      .filter((g) => ids.includes(g.gameID))
      .some((g) => now >= cutOffTime(g));
    if (someClosed) {
      setIsReloadNeeded(true);
    }
  }, [isRequesting, now]);

  useEffect(() => {
    if (!isRequesting && isReloadNeeded) {
      handleComeBackToMainScreen();
      setIsReloadNeeded(false);
    }
  }, [isRequesting, isReloadNeeded]);

  // refs
  const updateTransactionsRef = useRef(updateTransactions);
  useEffect(() => {
    updateTransactionsRef.current = updateTransactions;
  }, [updateTransactions]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const parentInfoRef = useRef(parentInfo);
  useEffect(() => {
    parentInfoRef.current = parentInfo;
  }, [parentInfo]);

  const nextGameIdRef = useRef(nextGame);
  useEffect(() => {
    nextGameIdRef.current = nextGame;
  }, [nextGame]);

  const selectedGameIdsRef = useRef(selectedGameIds);
  useEffect(() => {
    selectedGameIdsRef.current = selectedGameIds;
  }, [selectedGameIds]);

  const ticketsListRef = useRef(ticketsList);
  useEffect(() => {
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const betRef = useRef(bet);
  useEffect(() => {
    betRef.current = bet;
  }, [bet]);

  const cardsRef = useRef(cards);
  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  // batch sale queue: games are sold one-by-one (each its own transaction)
  const saleQueueRef = useRef([]);
  const pendingActionRef = useRef(null); // 'continue' | 'close'

  // derived data
  const cutOffTime = (g) => {
    const base = g.dateBuyEnd
      ? timeStringToTodayDate(g.dateBuyEnd)
      : timeStringToTodayDate(g.dateStart);
    const start = timeStringToTodayDate(g.dateStart);
    const explicitOrDefault = g.dateBuyEnd
      ? base
      : new Date(start.getTime() - CLOSE_BEFORE_MS);
    return Math.min(
      explicitOrDefault.getTime(),
      start.getTime() - CLOSE_BEFORE_MS
    );
  };

  const padTime = (t) => {
    if (t == null || t === '') return t;
    const [h = '0', m = '00'] = String(t).trim().split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  const openGames = useMemo(() => {
    const normalized = rawGames.map((g) => ({
      ...g,
      dateStart: padTime(g.dateStart),
      dateEnd: padTime(g.dateEnd),
    }));

    const closeMs = closeTime
      ? timeStringToTodayDate(closeTime).getTime()
      : null;

    return normalized.filter((g) => {
      if (cutOffTime(g) <= timeBase) return false;
      if (closeMs !== null) {
        const startMs = timeStringToTodayDate(g.dateStart).getTime();
        if (startMs >= closeMs) return false;
      }
      return true;
    });
  }, [rawGames, timeBase, closeTime]);

  const sortedOpenGames = useMemo(() => {
    return [...openGames].sort(
      (a, b) =>
        a.dateStart.localeCompare(b.dateStart) ||
        a.gameID - b.gameID ||
        a.name.localeCompare(b.name)
    );
  }, [openGames]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedOpenGames.length / GAMES_PER_PAGE)
  );

  const paginatedGames = useMemo(() => {
    const start = (gamesPage - 1) * GAMES_PER_PAGE;
    return sortedOpenGames.slice(start, start + GAMES_PER_PAGE);
  }, [sortedOpenGames, gamesPage]);

  useEffect(() => {
    setGamesPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const nextCutOff = useMemo(() => {
    const future = openGames.map(cutOffTime).sort((a, b) => a - b);
    return future[0] || null;
  }, [openGames]);

  const mmss = nextCutOff
    ? new Date(Math.max(0, nextCutOff - timeBase))
        .toISOString()
        .substring(14, 19)
    : '--:--';

  // drop any selected game once it closes
  useEffect(() => {
    setSelectedGameIds((prev) =>
      prev.filter((id) => openGames.some((g) => g.gameID === id))
    );
  }, [openGames]);

  useEffect(() => {
    if (openGames && openGames.length) {
      setNextGame(openGames[0].gameID);
    }
  }, [openGames]);

  // selection summary
  const selectedGames = openGames.filter((g) =>
    selectedGameIds.includes(g.gameID)
  );
  const selectionTotal = selectedGames.length * bet * cards;
  const selectedGamesLabel = [...selectedGameIds]
    .sort((a, b) => a - b)
    .join(', ');
  const canConfirm = selectedGameIds.length > 0 && bet > 0 && cards > 0;

  // handlers
  const handleComeBackToMainScreen = () => {
    if (ticketsListRef.current.length > 0) {
      socket.emit('newPapirXtippBatch', {
        printer: printerRef.current,
        bingoName: hallRef.current,
        ticketsList: ticketsListRef.current,
      });
      updateIsLoadingScreen(true);
      return;
    }
    setSelectedGameIds([]);
    setBet(0);
    setCards(0);
    setTicketList([]);
    ticketsListRef.current = [];
    updateIsPapirXtippOpen(false);
    updateIsSellerOpen(false);
  };

  const emitTask = (infoToEmit) => {
    updateIsLoadingScreen(true);
    setIsRequesting(true);
    console.log('Emitting newSalePapirXtipp with data:', infoToEmit);
    socket.emit('newSalePapirXtipp', infoToEmit);
  };

  const handleBetClick = (val) => {
    setBet(val);
  };

  const handleCardsClick = (val) => {
    setCards(val);
  };

  const toggleGame = (g) => {
    setSelectedGameIds((prev) =>
      prev.includes(g.gameID)
        ? prev.filter((id) => id !== g.gameID)
        : [...prev, g.gameID]
    );
  };

  const requestConfirm = (action) => {
    if (
      selectedGameIdsRef.current.length === 0 ||
      betRef.current === 0 ||
      cardsRef.current === 0
    ) {
      toast.error(t('sales.betAndCardsMustBeSelected'));
      return;
    }
    pendingActionRef.current = action; // 'continue' | 'close'
    setShowConfirm(true);
  };

  const handleModalCancel = () => {
    setShowConfirm(false);
    pendingActionRef.current = null;
  };

  const buildInfoToEmit = (item) => ({
    userID:
      parentInfoRef.current && parentInfoRef.current.id
        ? parentInfoRef.current.id
        : userInfoRef.current && userInfoRef.current.id
          ? userInfoRef.current.id
          : 0,
    user7Pincode:
      parentInfoRef.current && parentInfoRef.current['7 digit PIN']
        ? parentInfoRef.current['7 digit PIN']
        : userInfoRef.current && userInfoRef.current['7 digit PIN']
          ? userInfoRef.current['7 digit PIN']
          : 'Gjest',
    username: parentInfoRef.current
      ? parentInfoRef.current.username
      : userInfoRef.current.username,
    bingoName: hallRef.current,
    sessionID: sessionRef.current,
    gameID: item.gameID,
    isAnon: userInfoRef.current.username === 'Gjest' ? true : false,
    operator: operatorRef.current,
    name: item.name,
    priceCard: betRef.current,
    cards: cardsRef.current,
    printer: printerRef.current,
  });

  const processNextSale = () => {
    const queue = saleQueueRef.current;
    if (!queue.length) {
      finishBatch();
      return;
    }
    const item = queue.shift();
    emitTask(buildInfoToEmit(item));
  };

  const finishBatch = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    saleQueueRef.current = [];
    setSelectedGameIds([]);
    if (action === 'close') {
      // keep isRequesting/loading up through receipt print + close (no flicker)
      handleComeBackToMainScreen();
    } else {
      setIsRequesting(false);
      updateIsLoadingScreen(false);
    }
  };

  const handleModalConfirm = () => {
    setShowConfirm(false);
    const ids = selectedGameIdsRef.current;
    const queue = openGames
      .filter((g) => ids.includes(g.gameID))
      .map((g) => ({ gameID: g.gameID, name: g.name }));
    if (!queue.length) {
      pendingActionRef.current = null;
      return;
    }
    saleQueueRef.current = queue;
    processNextSale();
  };

  // addCards (keeps ticketsListRef in sync synchronously)
  function addCards(newCards) {
    const batch = Array.isArray(newCards) ? newCards : [newCards];
    const keyFor = (gameID, priceCard) => `${gameID}::${priceCard ?? 'null'}`;

    const byPair = new Map(
      ticketsListRef.current.map((g) => [
        keyFor(g.gameID, g.priceCard),
        { ...g, tickets: [...g.tickets] },
      ])
    );

    for (const item of batch) {
      const { gameID, cardNumber, cardPrice } = item;
      if (gameID == null || cardNumber == null) continue;

      const k = keyFor(gameID, cardPrice);
      const entry = byPair.get(k) || {
        gameID,
        tickets: [],
        priceCard: cardPrice ?? undefined,
      };

      if (!entry.tickets.includes(cardNumber)) {
        entry.tickets.push(cardNumber);
      }

      byPair.set(k, entry);
    }

    const next = Array.from(byPair.values())
      .map((g) => ({
        ...g,
        tickets: g.tickets.slice().sort((a, b) => a - b),
      }))
      .sort(
        (a, b) => a.gameID - b.gameID || (a.priceCard ?? 0) - (b.priceCard ?? 0)
      );

    ticketsListRef.current = next;
    setTicketList(next);
  }

  // socket
  useEffect(() => {
    socket.emit('getGamePlanXtipp', '');

    const handleGetGamePlan = (data) => {
      updateIsLoadingScreen(false);
      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      setRawGames(data);
    };

    const handleNewSalePapirXtipp = async (data) => {
      if (!data) {
        toast.error(t('error.error'));
        saleQueueRef.current = [];
        pendingActionRef.current = null;
        updateIsLoadingScreen(false);
        setIsRequesting(false);
        return;
      }

      addCards(data.cards);

      // one transaction line per game (bet x cards)
      updateTransactionsRef.current({
        name:
          'Ny Papir Xtipp Billett' +
          ' - ' +
          'CH (' +
          betRef.current +
          ' KR x' +
          cardsRef.current +
          ')',
        amount: parseInt(betRef.current * cardsRef.current),
        type: 'transaction',
      });

      processNextSale();
    };

    const handleNewPapirXtippBatch = (data) => {
      updateIsLoadingScreen(false);
      setSelectedGameIds([]);
      setBet(0);
      setCards(0);
      setTicketList([]);
      ticketsListRef.current = [];
      updateIsPapirXtippOpen(false);
      updateIsSellerOpen(false);
      setIsRequesting(false);
    };

    socket.on('getGamePlanXtipp', handleGetGamePlan);
    socket.on('newSalePapirXtipp', handleNewSalePapirXtipp);
    socket.on('newPapirXtippBatch', handleNewPapirXtippBatch);
    return () => {
      socket.off('getGamePlanXtipp', handleGetGamePlan);
      socket.off('newSalePapirXtipp', handleNewSalePapirXtipp);
      socket.off('newPapirXtippBatch', handleNewPapirXtippBatch);
    };
  }, [socket]);

  // Drag-to-scroll for the games grid; pointer events unify mouse and touch (and VNC)
  const gamesListRef = useRef(null);
  const gamesDrag = useRef({ active: false, startY: 0, startScrollTop: 0 });

  const handleGamesPointerDown = (event) => {
    const el = gamesListRef.current;
    if (!el) return;
    gamesDrag.current = {
      active: true,
      startY: event.clientY,
      startScrollTop: el.scrollTop,
    };
  };

  const handleGamesPointerMove = (event) => {
    const el = gamesListRef.current;
    if (!el || !gamesDrag.current.active) return;
    const delta = event.clientY - gamesDrag.current.startY;
    el.scrollTop = gamesDrag.current.startScrollTop - delta;
  };

  const endGamesDrag = () => {
    gamesDrag.current.active = false;
  };

  // render
  return (
    <Grid
      item
      xs={12}
      sx={{
        height: '100vh',
        p: 3,
        borderRadius: 4,
        background: 'transparent',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* title */}
      <Typography
        variant="h4"
        align="left"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <PrintIcon sx={{ fontSize: 40 }} />
        {t('sales.paper') + ' - Xtipp'}
      </Typography>

      {/* countdown */}
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography sx={{ fontSize: '1.2em' }}>
            {t('sales.countdownToNextGame1')}
            <strong>{nextGameIdRef.current}</strong>{' '}
            {t('sales.countdownToNextGame2')}
            <strong>{mmss}</strong>
          </Typography>
        </Grid>
      </Grid>

      {/* bet (sats) selector */}
      <Box sx={{ mt: 1.5 }}>
        <Typography
          sx={{
            color: '#FFC727',
            fontWeight: 900,
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 0.5,
          }}
        >
          {t('sales.bet')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          {BET_OPTIONS.map((n) => {
            const active = bet === n;
            return (
              <Button
                key={n}
                onClick={() => handleBetClick(n)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 56,
                  borderRadius: '12px',
                  fontWeight: 900,
                  fontSize: '20px',
                  textTransform: 'none',
                  color: active ? '#000' : '#fff',
                  bgcolor: active ? '#e3a406' : 'rgba(255,255,255,0.08)',
                  border: active
                    ? '1px solid #e3a406'
                    : '1px solid rgba(255,255,255,0.14)',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: active ? '#f0b41f' : 'rgba(255,255,255,0.16)',
                    boxShadow: 'none',
                  },
                }}
              >
                {n}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* cards (bonger) selector */}
      <Box sx={{ mt: 1.5 }}>
        <Typography
          sx={{
            color: '#FFC727',
            fontWeight: 900,
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 0.5,
          }}
        >
          {t('sales.cards')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          {CARD_OPTIONS.map((n) => {
            const active = cards === n;
            return (
              <Button
                key={n}
                onClick={() => handleCardsClick(n)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 56,
                  borderRadius: '12px',
                  fontWeight: 900,
                  fontSize: '20px',
                  textTransform: 'none',
                  color: active ? '#000' : '#fff',
                  bgcolor: active ? '#FFC727' : 'rgba(255,255,255,0.08)',
                  border: active
                    ? '1px solid #FFC727'
                    : '1px solid rgba(255,255,255,0.14)',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: active ? '#FFD54F' : 'rgba(255,255,255,0.16)',
                    boxShadow: 'none',
                  },
                }}
              >
                x{n}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* games grid (multi-select) */}
      <Box
        ref={gamesListRef}
        onPointerDown={handleGamesPointerDown}
        onPointerMove={handleGamesPointerMove}
        onPointerUp={endGamesDrag}
        onPointerLeave={endGamesDrag}
        onPointerCancel={endGamesDrag}
        sx={{
          mt: 2,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'safe center',
        }}
      >
        <Grid container spacing={1.5} justifyContent="flex-start">
          {paginatedGames.map((g) => {
            const selected = selectedGameIds.includes(g.gameID);
            const formattedName = g.name
              ? g.name.charAt(0).toUpperCase() + g.name.slice(1).toLowerCase()
              : '';
            return (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                key={`${g.gameID}-${g.dateStart}-${g.name}`}
              >
                <Button
                  onClick={() => toggleGame(g)}
                  variant={selected ? 'contained' : 'outlined'}
                  sx={{
                    width: '100%',
                    minHeight: 92,
                    borderColor: 'rgba(255,255,255,0.22)',
                    color: selected ? 'black' : '#fff',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                    textAlign: 'left',
                    px: 1.5,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'stretch',
                    justifyContent: 'space-between',
                    gap: 1.2,
                    borderRadius: '10px',
                    '&:hover': {
                      borderColor: '#fff',
                      bgcolor: selected
                        ? 'primary.dark'
                        : 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      minWidth: 0,
                      flex: 1,
                      gap: 0.3,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontWeight: 900, fontSize: '20px' }}
                    >
                      {t('kiosk.gameLabel')} {g.gameID}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        opacity: 0.9,
                        fontWeight: 700,
                        fontSize: '15px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {formattedName}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      minWidth: 78,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderLeft: '1px solid rgba(255,255,255,0.18)',
                      pl: 1.2,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 900,
                        fontSize: '22px',
                        lineHeight: 1,
                      }}
                    >
                      {g.dateStart}
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            );
          })}

          {!paginatedGames.length && (
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.72)',
                  textAlign: 'center',
                  py: 2,
                }}
              >
                {t('kiosk.noGamesAvailable')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ mt: 1.5, gap: 2 }}
      >
        <Button
          variant="contained"
          onClick={() => setGamesPage((p) => Math.max(1, p - 1))}
          disabled={gamesPage === 1}
          sx={{
            minWidth: '60px',
            height: '50px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: 'rgba(255,255,255,0.85)',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.16)',
              boxShadow: 'none',
            },
            '&:disabled': { opacity: 0.3 },
          }}
        >
          <ArrowLeftIcon sx={{ fontSize: '30px' }} />
        </Button>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.85)',
            fontWeight: 800,
            fontSize: '18px',
            minWidth: '90px',
            textAlign: 'center',
          }}
        >
          {t('kiosk.pageIndicator', {
            current: gamesPage,
            total: totalPages,
          })}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setGamesPage((p) => Math.min(totalPages, p + 1))}
          disabled={gamesPage === totalPages}
          sx={{
            minWidth: '60px',
            height: '50px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: 'rgba(255,255,255,0.85)',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.16)',
              boxShadow: 'none',
            },
            '&:disabled': { opacity: 0.3 },
          }}
        >
          <ArrowRightIcon sx={{ fontSize: '30px' }} />
        </Button>
      </Grid>

      {/* selection summary (full width) */}
      <Box
        sx={{
          mt: 1.5,
          width: '100%',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.14)',
          background: 'rgba(255,255,255,0.06)',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '20px',
            textAlign: 'center',
            color: canConfirm ? '#fff' : 'rgba(255,255,255,0.55)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {canConfirm
            ? `${cards} ${t('sales.cards')} · ${t('sales.bet')} ${bet} · ${t('kiosk.gameLabel')} ${selectedGamesLabel}  =  ${selectionTotal} kr`
            : t('sales.selectGameAndCards')}
        </Typography>
      </Box>

      {/* action buttons (bottom) — same aesthetic as the comeback button */}
      <Box
        sx={{
          height: '120px',
          mt: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <button
          className="btn-custom-postpone btn-label-wide"
          onClick={() => {
            if (ticketsList.length === 0) handleComeBackToMainScreen();
          }}
          disabled={ticketsList.length > 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
            opacity: ticketsList.length > 0 ? 0.4 : 1,
            cursor: ticketsList.length > 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <ArrowBackIcon style={{ fontSize: '20px' }} />
          <span>{t('sales.backToMainScreen')}</span>
        </button>

        <button
          className="btn-custom-yes-and-stay btn-label-wide"
          onClick={() => requestConfirm('continue')}
          disabled={!canConfirm}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
            opacity: canConfirm ? 1 : 0.4,
            cursor: canConfirm ? 'pointer' : 'not-allowed',
          }}
        >
          <AddCircleIcon style={{ fontSize: '20px' }} />
          <span>{t('sales.confirmAndContinue')}</span>
        </button>

        <button
          className="btn-custom-yes btn-label-wide"
          onClick={() => requestConfirm('close')}
          disabled={!canConfirm}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
            opacity: canConfirm ? 1 : 0.4,
            cursor: canConfirm ? 'pointer' : 'not-allowed',
          }}
        >
          <CheckCircleIcon style={{ fontSize: '20px' }} />
          <span>{t('sales.confirmAndClose')}</span>
        </button>

        {ticketsList.length > 0 && !canConfirm && !isRequesting && (
          <button
            className="btn-custom-yes btn-label-wide"
            onClick={() => handleComeBackToMainScreen()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
            }}
          >
            <PrintIcon style={{ fontSize: '20px' }} />
            <span>{t('sales.done')}</span>
          </button>
        )}
      </Box>

      {/* confirmation modal */}
      {showConfirm && (
        <div id="react-confirm-alert">
          <div className="react-confirm-alert-overlay">
            <div className="react-confirm-alert">
              <div
                className="react-confirm-alert-body"
                style={{ width: '1000px' }}
              >
                <h1>{t('sales.confirmKingoSaleTitle')}</h1>
                <p style={{ fontSize: '22px', marginTop: '20px' }}>
                  <Trans
                    i18nKey="sales.confirmBatchWithBet"
                    values={{
                      cards: cards,
                      bet: bet,
                      games: selectedGamesLabel,
                      total: selectionTotal,
                    }}
                    components={{
                      hl: (
                        <strong
                          style={{ color: '#42a5f5', fontWeight: 'bold' }}
                        />
                      ),
                    }}
                  />
                </p>
                <div
                  className="react-confirm-alert-button-group"
                  style={{ marginTop: '55px' }}
                >
                  <button
                    className="btn-custom-yes"
                    onClick={() => handleModalConfirm()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.confirm')}
                  </button>
                  <button
                    className="btn-custom-no"
                    onClick={() => handleModalCancel()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CancelIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Grid>
  );
}

export default PaymentScreen;
